import { useCallback, useRef, useState } from 'react';

import { useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { ACCEPTED_EXTENSIONS, STATUS_STYLES } from './helpers';
import { useParseLoadFileHeaderSample } from '@/api/mutations/useParseLoadFileHeaderSample';
import { useUploadLoadFileImport } from '@/api/mutations/useUploadLoadFileImport';
import useGetLoadFileImports, { type LoadFileImportListItem } from '@/api/queries/useGetLoadFileImports';
import { QueryEntity } from '@/api/types';
import { Icon } from '@/new/components/icon';
import {
  SettingsBackLink,
  SettingsContent,
  SettingsListItem,
  SettingsSection,
  SettingsSectionHeading,
} from '@/new/components/settings-section';
import { cn } from '@/new/lib/utils';

const DATE_FORMAT: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
};

export function LoadFileImportLanding() {
  const { matterId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const { mutateAsync: uploadLoadFileImport, isLoading: isUploading } = useUploadLoadFileImport();
  const { mutateAsync: parseHeaderSample, isLoading: isParsing } = useParseLoadFileHeaderSample();
  const { data: imports, isLoading: isLoadingImports } = useGetLoadFileImports(matterId);

  const isBusy = isUploading || isParsing;

  const handleFile = useCallback(
    async (file: File) => {
      if (!matterId) return;
      try {
        const { importId } = await uploadLoadFileImport({ matterId, file });
        const parseResult = await parseHeaderSample({ matterId, importId });
        // Hand the freshly parsed preview to the wizard so it doesn't re-parse.
        navigate(importId, { state: { parseResult, delimiters: parseResult.detectedDelimiters } });
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Failed to upload load file');
        // The upload may have created the import row (now failed) — show it.
        void queryClient.invalidateQueries([QueryEntity.LoadFileImports]);
      }
    },
    [matterId, uploadLoadFileImport, parseHeaderSample, navigate, queryClient],
  );

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      setIsDragging(false);
      const file = event.dataTransfer.files?.[0];
      if (file) void handleFile(file);
    },
    [handleFile],
  );

  if (!matterId) return null;

  return (
    <>
      <SettingsBackLink to=".." label="Back to Relativity" />

      <SettingsContent>
        <div className="space-y-6">
          <SettingsSectionHeading
            title="Import a load file"
            description="Upload a Relativity load file to map its columns onto this Matter's documents."
            inset={false}
          />

          <button
            type="button"
            disabled={isBusy}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={onDrop}
            className={cn(
              'group flex w-full flex-col items-center justify-center gap-3 rounded-xl border border-dashed px-6 py-12 text-center transition-colors',
              isDragging
                ? 'border-teal-400 bg-teal-50'
                : 'border-gray-300 bg-gray-50 hover:border-gray-400 hover:bg-gray-100/70',
              isBusy && 'pointer-events-none opacity-60',
            )}
          >
            {isBusy ? (
              <>
                <span className="flex size-10 items-center justify-center rounded-lg border bg-white text-sm">
                  <Icon className="lucide--loader size-5 animate-spin text-gray-400" />
                </span>
                <span className="text-sm text-gray-500">{isUploading ? 'Uploading...' : 'Parsing file...'}</span>
              </>
            ) : (
              <>
                <div className="flex items-center justify-center rounded-lg border bg-white p-2 text-base duration-150 group-hover:border-gray-300">
                  <Icon className="lucide--upload text-gray-400" />
                </div>

                <div className="space-y-1">
                  <p className="text-sm text-gray-700">
                    Drop a load file here, or <span className="text-blue-500">browse</span>
                  </p>
                  <p className="text-xs text-gray-400">Accepts .dat, .txt and .csv files</p>
                </div>
              </>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_EXTENSIONS}
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void handleFile(file);
                e.target.value = '';
              }}
            />
          </button>
        </div>

        {imports && imports.length > 0 ? (
          <SettingsSection title="Recent imports">
            {imports.map((item) => (
              <RecentImportRow key={item.id} item={item} onOpen={() => navigate(item.id)} />
            ))}
          </SettingsSection>
        ) : (
          <div className="space-y-2">
            <SettingsSectionHeading title="Recent imports" inset={false} />
            {isLoadingImports ? (
              <p className="flex items-center gap-2 py-2 text-sm text-gray-400">
                <Icon className="lucide--loader animate-spin ease-in-out" />
                Loading imports...
              </p>
            ) : (
              <p className="py-2 text-sm text-gray-400">No imports yet. Upload a load file to get started.</p>
            )}
          </div>
        )}
      </SettingsContent>
    </>
  );
}

function RecentImportRow({ item, onOpen }: { item: LoadFileImportListItem; onOpen: () => void }) {
  const createdDate = new Date(item.createdDate).toLocaleString(undefined, DATE_FORMAT);
  const showCounts = item.status === 'completed' || item.status === 'processing';
  const isFailed = item.status === 'failed';

  return (
    <SettingsListItem
      title={item.fileName}
      description={
        showCounts
          ? `${item.matchedRows.toLocaleString()} matched · ${item.unmatchedRows.toLocaleString()} unmatched · ${item.ambiguousRows.toLocaleString()} ambiguous`
          : (item.errorReason ?? createdDate)
      }
      actions={
        <div className="flex items-center gap-3">
          <span className={cn('rounded px-2 py-0.5 text-xs font-medium capitalize', STATUS_STYLES[item.status])}>
            {item.status}
          </span>
          {!isFailed && <Icon className="lucide--chevron-right text-gray-400" />}
        </div>
      }
      onClick={isFailed ? undefined : onOpen}
    />
  );
}
